/**
 * 社内/社外 (internal/external) detection + role resolution from whoami
 * permissions, shared by the backend (resolveInternal middleware) and the
 * frontend (useIsExternalUser) so the two can never diverge.
 *
 * HOME does not know sekosuryo's app permissions — its whoami only carries the
 * role name on the grant's `conditions.name` (e.g. `internal.operator3`). sekosuryo
 * maps that name → `sekosuryo.*` permissions in code (see seed-roles). A user is
 * 社内 for an org when a role that applies to that org is not an `external.*`
 * role; everyone else is 社外.
 */

/**
 * A permission or group as it appears in the whoami result: an optional string
 * `scope`, an optional `conditions` map (which may carry `name`), and (for
 * groups) nested `permissions`.
 */
export interface WhoamiPermissionEntry {
  scope?: unknown;
  conditions?: Record<string, unknown>;
  permissions?: unknown[];
}

/**
 * Whether an entry's scope applies to the acting org and app. An `undefined`
 * scope inherits the enclosing group's scope (already gated one level up). A
 * string scope applies only when it is exactly `org:<id>:app:<appId>` — the
 * grant must name both the acting org and this app. A bare org scope
 * (`org:<id>`, no app segment), a different org, or this org scoped to a
 * different app is rejected (anchored match, not substring).
 */
export function scopeAppliesToOrg(scope: unknown, orgId: string, appId: string): boolean {
  if (scope === undefined) return true;
  if (typeof scope !== 'string') return false;
  return scope === `org:${orgId}:app:${appId}`;
}

/**
 * All role names that apply to `orgId` and `appId`, deduped. A role name is
 * read from an entry's `conditions.name`; a user may hold more than one role
 * for the same org+app, so every applicable name is collected. An entry scoped
 * to another org, or to another app within this org, is skipped along with its
 * whole subtree (children inherit the parent scope).
 */
export function roleNamesForOrg(
  permissions: readonly unknown[],
  orgId: string,
  appId: string
): string[] {
  const names = new Set<string>();
  const walk = (entries: readonly unknown[]) => {
    for (const raw of entries) {
      if (!raw || typeof raw !== 'object') continue;
      const entry = raw as WhoamiPermissionEntry;
      if (!scopeAppliesToOrg(entry.scope, orgId, appId)) continue;
      const name = entry.conditions?.name;
      if (typeof name === 'string' && name.length > 0) names.add(name);
      if (Array.isArray(entry.permissions)) walk(entry.permissions);
    }
  };
  walk(permissions);
  return [...names];
}

/** A role name marks 社外 when it is an `external.*` role. */
export function isExternalRole(name: string): boolean {
  return name.startsWith('external.');
}

/**
 * Whether a user counts as 社外 from their role names — true when any role is an
 * `external.*` role. Internal-only users, and users with no roles, are 社内.
 * Used to gate the 社外ユーザ picker/grant so only external users are offered
 * or accepted; frontend and backend share this predicate so they cannot diverge.
 */
export function isExternalUser(roleNames: readonly string[]): boolean {
  return roleNames.some(isExternalRole);
}

/** Resolution of a caller's roles for one org. */
export interface OrgRoleResolution {
  /** Effective role names — external-only when the caller holds both kinds. */
  roles: string[];
  /** Every role name that applied to the org, before the mixed-role downgrade. */
  allNames: string[];
  /** 社内 when the caller holds an internal role and no external role. */
  isInternal: boolean;
  /** True when both internal and external roles applied — a config conflict. */
  isMixed: boolean;
}

/**
 * Resolve the caller's roles for `orgId` and `appId`. Holding both an internal
 * and an external role for the same org+app is a misconfiguration; we fail
 * closed to the least privilege — external only, `isInternal=false` — and flag
 * `isMixed` so the caller can warn. Otherwise 社内 iff any applicable role is
 * not external.
 */
export function resolveOrgRoles(
  permissions: readonly unknown[],
  orgId: string,
  appId: string
): OrgRoleResolution {
  const allNames = roleNamesForOrg(permissions, orgId, appId);
  const external = allNames.filter(isExternalRole);
  const internal = allNames.filter((name) => !isExternalRole(name));
  if (external.length > 0 && internal.length > 0) {
    return { roles: external, allNames, isInternal: false, isMixed: true };
  }
  return { roles: allNames, allNames, isInternal: internal.length > 0, isMixed: false };
}

/**
 * 社内 (internal) for `orgId` and `appId`. Fails closed: no applicable role,
 * or a mix of internal and external roles, reads as 社外. See `resolveOrgRoles`.
 */
export function isInternalForOrg(
  permissions: readonly unknown[],
  orgId: string,
  appId: string
): boolean {
  return resolveOrgRoles(permissions, orgId, appId).isInternal;
}
